import React, { useEffect, useMemo, useState } from 'react';
import { Box, Container, Typography, Button, Stack, Chip, Skeleton, IconButton } from '@mui/material';
import {
  LocalShipping, ShoppingBasket, RestaurantMenu, Verified, ArrowForward, AccessTime,
  Instagram, LinkedIn, Facebook, Language,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { loadMenu } from '../lib/menu';
import { DEMO_TENANT_ID } from '../supabase';
import { useLang, pickLocale } from '../i18n/LanguageProvider';
import { useReveal } from '../lib/useReveal';
import { palette } from '../theme';
import type { Category, MenuItem } from '../types';

const fmt = (n: number) => `${n.toFixed(2).replace('.', ',')} EUR`;

function isOpenNow(): boolean {
  const now = new Date();
  const h = now.getHours();
  const m = now.getMinutes();
  const mins = h * 60 + m;
  const lunch  = mins >= 11 * 60 + 30 && mins <= 14 * 60 + 30;
  const dinner = mins >= 17 * 60      && mins <= 22 * 60 + 30;
  return now.getDay() !== 1 && (lunch || dinner);
}

export const LandingPage: React.FC = () => {
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState<MenuItem[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [sublineIdx, setSublineIdx] = useState(0);
  const open = useMemo(isOpenNow, []);

  const sublines = ['hero.sub.0', 'hero.sub.1', 'hero.sub.2'] as const;

  useEffect(() => {
    (async () => {
      try {
        const data = await loadMenu(DEMO_TENANT_ID);
        setCategories(data.categories);
        setItems(data.items);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    const t = setInterval(() => setSublineIdx((i) => (i + 1) % sublines.length), 3800);
    return () => clearInterval(t);
  }, []);

  const featured = useMemo(
    () =>
      [...items]
        .filter((i) => i.image_url && i.is_available)
        .sort((a, b) => ((b.tags ?? []).includes('popular') ? 1 : 0) - ((a.tags ?? []).includes('popular') ? 1 : 0))
        .slice(0, 6),
    [items],
  );

  // Pick category images from any item belonging to that category
  const categoryTiles = useMemo(() => {
    const itemImageByCat = new Map<string, string>();
    items.forEach((it) => {
      if (it.image_url && it.category_id && !itemImageByCat.has(it.category_id)) {
        itemImageByCat.set(it.category_id, it.image_url);
      }
    });
    return categories
      .map((c) => ({ ...c, _img: c.image_url || itemImageByCat.get(c.id) || null }))
      .filter((c) => c._img)
      .slice(0, 6);
  }, [categories, items]);

  const usp = useReveal();
  const cats = useReveal();
  const feat = useReveal();

  return (
    <Box>
      {/* ───────── HERO ───────── */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: '88vh', sm: '90vh' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          background: `radial-gradient(ellipse at 25% 25%, ${palette.bgElev} 0%, ${palette.bg} 60%, #050B14 100%)`,
        }}
        className="hero-bg"
      >
        <Box className="noise" />
        <Container maxWidth="md" sx={{ position: 'relative', zIndex: 3, textAlign: 'center', pt: 8, pb: 6 }}>
          <Box
            component="img"
            src="/saheeh_logo_w.svg"
            alt="Saheeh"
            sx={{
              width: { xs: 100, sm: 140 },
              height: { xs: 100, sm: 140 },
              mb: 3,
              filter: `drop-shadow(0 16px 40px ${palette.primary}66)`,
              animation: 'saheehFloat 5s ease-in-out infinite',
            }}
          />
          <Typography
            sx={{
              color: palette.primary,
              fontSize: '12px',
              letterSpacing: '4px',
              fontWeight: 700,
              textTransform: 'uppercase',
              mb: 1.5,
              animation: 'saheehFadeIn 0.8s ease-out backwards',
            }}
          >
            {t('hero.kicker')}
          </Typography>
          <Typography
            variant="h1"
            sx={{
              color: '#fff',
              fontSize: lang === 'ar'
                ? { xs: '34px', sm: '46px', md: '60px' }
                : { xs: '40px', sm: '56px', md: '72px' },
              lineHeight: 1.15,
              fontWeight: 800,
              mb: 2,
              px: 1,
              wordBreak: 'normal',
              overflowWrap: 'break-word',
              animation: 'saheehFadeUp 0.9s cubic-bezier(.22,1,.36,1) 0.1s backwards',
            }}
          >
            {t('hero.tagline')}
          </Typography>
          <Typography
            sx={{
              color: palette.textMuted,
              fontSize: { xs: '16px', sm: '18px' },
              minHeight: '28px',
              mb: 4,
              transition: 'opacity .4s',
              key: sublineIdx,
              animation: 'saheehFadeIn 0.6s ease-out',
            }}
            key={sublineIdx}
          >
            {t(sublines[sublineIdx])}
          </Typography>

          <Chip
            icon={<AccessTime sx={{ fontSize: '14px !important' }} />}
            label={open ? t('hero.open_now') : t('hero.closed')}
            sx={{
              mb: 4,
              backgroundColor: open ? 'rgba(0,200,150,0.15)' : 'rgba(212,175,55,0.15)',
              color: open ? palette.primary : palette.gold,
              border: `1px solid ${open ? palette.primary : palette.gold}55`,
              fontWeight: 600,
              animation: 'saheehFadeIn 1s ease-out 0.3s backwards',
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{
              justifyContent: 'center',
              animation: 'saheehFadeUp 0.9s cubic-bezier(.22,1,.36,1) 0.4s backwards',
            }}
          >
            <Button
              variant="contained"
              size="large"
              startIcon={<LocalShipping />}
              onClick={() => navigate('/menu')}
              sx={{
                py: 1.5,
                px: 4,
                gap: 1,
                fontWeight: 700,
                fontSize: '15px',
                background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryDark} 100%)`,
                color: palette.bg,
                boxShadow: `0 12px 32px ${palette.primary}44`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${palette.primaryHoverHi} 0%, ${palette.primaryHoverLo} 100%)`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 16px 40px ${palette.primary}66`,
                },
                transition: 'all .3s',
              }}
            >
              {t('hero.cta.order_delivery')}
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<ShoppingBasket />}
              onClick={() => navigate('/menu')}
              sx={{
                py: 1.5,
                px: 4,
                gap: 1,
                fontWeight: 600,
                fontSize: '15px',
                color: '#fff',
                borderColor: palette.border,
                background: 'rgba(255,255,255,0.04)',
                '&:hover': {
                  borderColor: palette.primary,
                  background: 'rgba(0,200,150,0.08)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all .3s',
              }}
            >
              {t('hero.cta.order_pickup')}
            </Button>
          </Stack>

          {/* ───── Social row — visible right in the hero ───── */}
          <Stack
            direction="row"
            spacing={1}
            justifyContent="center"
            alignItems="center"
            sx={{
              mt: 4,
              flexWrap: 'wrap',
              animation: 'saheehFadeIn 1s ease-out 0.7s backwards',
            }}
          >
            {[
              { Icon: Language,  label: t('footer.website'),   href: 'https://www.saheehpos.com/',                                       caption: 'saheehpos.com' },
              { Icon: Instagram, label: t('social.instagram'), href: 'https://www.instagram.com/blacklayerspro/',                        caption: '@blacklayerspro' },
              { Icon: LinkedIn,  label: t('social.linkedin'),  href: 'https://www.linkedin.com/in/black-layers-a7ba35357/',              caption: 'LinkedIn' },
              { Icon: Facebook,  label: t('social.facebook'),  href: 'https://www.facebook.com/profile.php?id=61574805680907',           caption: 'Facebook' },
            ].map(({ Icon, label, href, caption }) => (
              <Button
                key={label}
                component="a"
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                startIcon={<Icon sx={{ fontSize: '18px !important' }} />}
                sx={{
                  py: 0.75,
                  px: 1.75,
                  minHeight: 36,
                  fontWeight: 600,
                  fontSize: '12px',
                  textTransform: 'none',
                  color: palette.textMuted,
                  borderRadius: 999,
                  border: `1px solid ${palette.border}`,
                  background: 'rgba(255,255,255,0.04)',
                  letterSpacing: '0.2px',
                  transition: 'all .2s',
                  '&:hover': {
                    color: palette.primary,
                    borderColor: palette.primary,
                    background: 'rgba(0,200,150,0.10)',
                    transform: 'translateY(-1px)',
                  },
                  '& .MuiButton-startIcon': { mr: 0.75, ml: 0 },
                }}
              >
                {caption}
              </Button>
            ))}
          </Stack>
        </Container>
      </Box>

      {/* ───────── USPs ───────── */}
      <Container
        maxWidth="lg"
        ref={usp.ref as React.RefObject<HTMLDivElement>}
        className={`reveal ${usp.visible ? 'visible' : ''}`}
        sx={{ py: { xs: 5, sm: 8 } }}
      >
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
          {[
            { Icon: RestaurantMenu, title: t('usp.fresh.title'),    body: t('usp.fresh.body') },
            { Icon: LocalShipping,  title: t('usp.delivery.title'), body: t('usp.delivery.body') },
            { Icon: Verified,       title: t('usp.secure.title'),   body: t('usp.secure.body') },
          ].map(({ Icon, title, body }) => (
            <Box
              key={title}
              sx={{
                p: 3,
                borderRadius: 3,
                border: `1px solid ${palette.border}`,
                background: palette.bgPanel,
                backdropFilter: 'blur(20px)',
              }}
            >
              <Icon sx={{ color: palette.primary, fontSize: '32px', mb: 1.5 }} />
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '17px', mb: 0.75 }}>
                {title}
              </Typography>
              <Typography sx={{ color: palette.textMuted, fontSize: '14px', lineHeight: 1.5 }}>
                {body}
              </Typography>
            </Box>
          ))}
        </Box>
      </Container>

      {/* ───────── CATEGORIES ───────── */}
      {(loading || categoryTiles.length > 0) && (
        <Container
          maxWidth="lg"
          ref={cats.ref as React.RefObject<HTMLDivElement>}
          className={`reveal ${cats.visible ? 'visible' : ''}`}
          sx={{ py: { xs: 4, sm: 6 } }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-end" sx={{ mb: 3, gap: 2 }}>
            <Typography variant="h2" sx={{ color: '#fff', fontSize: { xs: '28px', sm: '36px' } }}>
              {t('categories.title')}
            </Typography>
            <Button
              endIcon={<ArrowForward />}
              onClick={() => navigate('/menu')}
              sx={{ color: palette.primary, fontWeight: 600, '&:hover': { background: 'rgba(0,200,150,0.08)' } }}
            >
              {t('categories.view_all')}
            </Button>
          </Stack>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(6, 1fr)' }, gap: 1.5 }}>
            {loading
              ? Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} variant="rounded" sx={{ aspectRatio: '1/1', borderRadius: 2, backgroundColor: palette.bgElev }} />
                ))
              : categoryTiles.map((c) => (
                  <Box
                    key={c.id}
                    onClick={() => navigate(`/menu?category=${encodeURIComponent(c.id)}`)}
                    className="lift"
                    sx={{
                      position: 'relative',
                      aspectRatio: '1/1',
                      borderRadius: 2.5,
                      overflow: 'hidden',
                      cursor: 'pointer',
                      border: `1px solid ${palette.border}`,
                      '&:hover': { borderColor: palette.primary },
                    }}
                  >
                    <Box
                      component="img"
                      src={c._img!}
                      alt={pickLocale(c, lang)}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: `linear-gradient(180deg, transparent 30%, rgba(5,11,20,0.95) 100%)`,
                        display: 'flex',
                        alignItems: 'flex-end',
                        p: 1.5,
                      }}
                    >
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: { xs: '13px', sm: '14px' }, lineHeight: 1.2 }}>
                        {pickLocale(c, lang)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
          </Box>
        </Container>
      )}

      {/* ───────── FEATURED ───────── */}
      {(loading || featured.length > 0) && (
        <Container
          maxWidth="lg"
          ref={feat.ref as React.RefObject<HTMLDivElement>}
          className={`reveal ${feat.visible ? 'visible' : ''}`}
          sx={{ py: { xs: 4, sm: 6 } }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography variant="h2" sx={{ color: '#fff', fontSize: { xs: '28px', sm: '36px' }, mb: 0.5 }}>
              {t('featured.title')}
            </Typography>
            <Typography sx={{ color: palette.textMuted, fontSize: '15px' }}>
              {t('featured.subtitle')}
            </Typography>
          </Box>

          <Box
            className="no-scrollbar"
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              pb: 2,
              mx: { xs: -2, sm: 0 },
              px: { xs: 2, sm: 0 },
              scrollSnapType: 'x mandatory',
            }}
          >
            {(loading ? Array.from({ length: 6 }) : featured).map((it: any, idx) => (
              <Box
                key={loading ? idx : it.id}
                onClick={() => !loading && navigate('/menu')}
                sx={{
                  flex: '0 0 auto',
                  width: { xs: 220, sm: 260 },
                  scrollSnapAlign: 'start',
                  cursor: 'pointer',
                }}
              >
                {loading ? (
                  <Skeleton variant="rounded" sx={{ width: '100%', aspectRatio: '4/5', borderRadius: 3, backgroundColor: palette.bgElev }} />
                ) : (
                  <Box
                    className="lift"
                    sx={{
                      position: 'relative',
                      borderRadius: 3,
                      overflow: 'hidden',
                      border: `1px solid ${palette.border}`,
                    }}
                  >
                    <Box
                      component="img"
                      src={it.image_url}
                      alt={it.name}
                      sx={{ width: '100%', aspectRatio: '4/5', objectFit: 'cover', display: 'block' }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        background: `linear-gradient(180deg, transparent 40%, rgba(5,11,20,0.98) 100%)`,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        p: 2,
                      }}
                    >
                      <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '15px', mb: 0.5 }}>
                        {pickLocale(it, lang)}
                      </Typography>
                      <Typography sx={{ color: palette.primary, fontWeight: 700 }}>
                        {fmt(Number(it.base_price) || 0)}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        </Container>
      )}

      {/* ───────── FINAL CTA ───────── */}
      <Container maxWidth="md" sx={{ py: { xs: 5, sm: 8 }, textAlign: 'center' }}>
        <Typography variant="h2" sx={{ color: '#fff', fontSize: { xs: '28px', sm: '36px' }, mb: 2 }}>
          {t('menu.title')}
        </Typography>
        <Button
          variant="contained"
          size="large"
          endIcon={<ArrowForward />}
          onClick={() => navigate('/menu')}
          sx={{
            py: 1.5,
            px: 5,
            fontWeight: 700,
            fontSize: '15px',
            background: `linear-gradient(135deg, ${palette.primary} 0%, ${palette.primaryDark} 100%)`,
            color: palette.bg,
            '&:hover': {
              background: `linear-gradient(135deg, ${palette.primaryHoverHi} 0%, ${palette.primaryHoverLo} 100%)`,
              transform: 'translateY(-2px)',
              boxShadow: `0 16px 40px ${palette.primary}55`,
            },
            transition: 'all .3s',
          }}
        >
          {t('categories.view_all')}
        </Button>
      </Container>

      {/* ───────── FOOTER ───────── */}
      <Box sx={{ borderTop: `1px solid ${palette.border}`, mt: 4, py: { xs: 5, sm: 6 } }}>
        <Container maxWidth="md">
          <Stack alignItems="center" spacing={3}>
            {/* Brand mark */}
            <Box
              component="img"
              src="/saheeh_logo_w.svg"
              alt="Saheeh"
              sx={{ width: 48, height: 48, opacity: 0.95 }}
            />

            {/* Website */}
            <Button
              component="a"
              href="https://www.saheehpos.com/"
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Language />}
              sx={{
                color: '#fff',
                fontWeight: 600,
                fontSize: '14px',
                textTransform: 'none',
                px: 2,
                py: 0.75,
                borderRadius: 999,
                border: `1px solid ${palette.border}`,
                background: 'rgba(255,255,255,0.03)',
                '&:hover': {
                  borderColor: palette.primary,
                  background: 'rgba(0,200,150,0.08)',
                  color: palette.primary,
                },
                '& .MuiButton-startIcon': { mr: 1, ml: 0 },
              }}
            >
              saheehpos.com
            </Button>

            {/* Social row */}
            <Stack
              direction="row"
              spacing={1.5}
              alignItems="center"
              sx={{ pt: 1 }}
            >
              <Typography sx={{ color: palette.textMuted, fontSize: '12px', letterSpacing: '2px', textTransform: 'uppercase', mx: 1 }}>
                {t('footer.follow')}
              </Typography>
              {[
                { Icon: Instagram, label: t('social.instagram'), href: 'https://www.instagram.com/blacklayerspro/' },
                { Icon: LinkedIn,  label: t('social.linkedin'),  href: 'https://www.linkedin.com/in/black-layers-a7ba35357/' },
                { Icon: Facebook,  label: t('social.facebook'),  href: 'https://www.facebook.com/profile.php?id=61574805680907' },
              ].map(({ Icon, label, href }) => (
                <IconButton
                  key={label}
                  component="a"
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  sx={{
                    color: palette.textMuted,
                    width: 40,
                    height: 40,
                    border: `1px solid ${palette.border}`,
                    background: 'rgba(255,255,255,0.03)',
                    transition: 'all 0.2s',
                    '&:hover': {
                      color: palette.primary,
                      borderColor: palette.primary,
                      background: 'rgba(0,200,150,0.10)',
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  <Icon sx={{ fontSize: 20 }} />
                </IconButton>
              ))}
            </Stack>

            {/* Compliance + powered + rights */}
            <Stack spacing={0.5} alignItems="center" sx={{ pt: 1 }}>
              <Typography sx={{ color: palette.textFaint, fontSize: '12px', letterSpacing: '1px' }}>
                {t('footer.compliance')}
              </Typography>
              <Typography sx={{ color: palette.primary, fontSize: '13px', fontWeight: 700 }}>
                {t('footer.powered')}{' '}
                <Box
                  component="a"
                  href="https://www.saheehpos.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'inherit', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  BlackLayers
                </Box>
              </Typography>
              <Typography sx={{ color: palette.textFaint, fontSize: '11px', mt: 1 }}>
                © {new Date().getFullYear()} BlackLayers · {t('footer.rights')}
              </Typography>
            </Stack>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};
